import { eventTypes, logLevel, statuses } from './enums';

const defaultConfig = {
	logLevel: logLevel.warning,
};

/***
 * Interface for vendor integration
 * @interface
 */
export const Connection = function (_config) {
	const config = Object.assign({}, defaultConfig, _config);

	/** @member {number} */
	this.status = statuses.INITIALIZED;
	/** @member {number} */
	this.reconnectAttempts = 0;

	this.logLevel = config.logLevel;

	this.observers = {
		[eventTypes.connected]: [],
		[eventTypes.disconnected]: [],
		[eventTypes.reconnected]: [],
		[eventTypes.notification]: [],
		[eventTypes.progress]: [],
		[eventTypes.message]: [],
		[eventTypes.statusChanged]: [],
		[eventTypes.error]: [],
	};
};

/**
 * Establish a connection to the server.
 *
 * @function
 * @name start
 * @returns {Promise} A promise representing a connection being made to the
 *   server.
 */

/**
 * Disconnect from the server.
 *
 * @function
 * @name stop
 */

/**
 * Subscribe to an event. Doesn't allow duplicate listeners
 * @param {string} eventName - The event to which to subscribe
 * @param {function} callback - The listener to add
 * @returns {boolean} whether the listener was added
 */
Connection.prototype.subscribe = function (eventName, callback) {
	if (!this.observers[eventName]) {
		throw new Error(`Event name '${eventName}' invalid`);
	}

	// don't allow duplicate listeners
	if (this.observers[eventName].includes(callback)) {
		return false;
	}

	this.observers[eventName].push(callback);
	return true;
};

/**
 * Unsubscribe a listener from an event
 * @param {string} eventName - The event to which to unsubscribe
 * @param {function} callback - The listener to remove
 * @returns {boolean} whether the listener was removed
 */
Connection.prototype.unsubscribe = function (eventName, callback) {
	const observers = this.observers[eventName];

	if (!observers) {
		throw new Error(`Event name '${eventName}' invalid`);
	}

	// only remove first occurance?
	const index = this.observers[eventName].indexOf(callback);

	if (index === -1) {
		return false;
	}

	this.observers[eventName].splice(index, 1);
	return true;
};

/**
 * Fire an event
 * @param {string} eventName - The event to fire
 * @param  {...any} args - Arguments to pass to listeners
 */
Connection.prototype.fire = function (eventName, ...args) {
	this.log(logLevel.trace, `Event '${eventName}' fired with args:`, args);

	const observers = this.observers[eventName];

	if (!observers) {
		throw new Error(`Event name '${eventName}' invalid`);
	}

	for (const element of observers) {
		element(...args);
	}
};

/**
 * Set the status of this connection
 * @param {number} status - Set the status of this connection and fire the
 *   'statusChanged' event
 */
Connection.prototype.setStatus = function (status) {
	if (status === this.status) {
		return;
	}

	this.fire('statusChanged', this.status, status);
	this.status = status;
};

Connection.prototype.log = function (loglvl) {
	if (loglvl > this.logLevel) {
		return;
	}
};
